import { BasicButton, GhostButton, OutlineButton } from '../../../../widgets/buttons';
import { DropdownActionItem, DropdownActions } from '../../../../widgets/dropdown';
import { Dialog, useDialogManager } from '../../../../widgets/dialog';
import { Icon } from '../../../../widgets/icon';
import { ZTooltip } from '../../../../widgets/ztooltip';
import React, { FC, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from 'react-relay';
import ArrowBackIcon from 'src/shared/material_icons/navigation/arrow_back.svg';
import EditIcon from 'src/shared/material_icons/image/edit.svg';
import ExpandIcon from 'src/shared/material_icons/navigation/expand_more.svg';
import CollapseIcon from 'src/shared/material_icons/navigation/expand_less.svg';
import SaveIcon from 'src/shared/material_icons/content/save.svg';
import PlayIcon from 'src/shared/material_icons/av/play_arrow.svg';
import ProductLogicIcon from 'src/shared/material_icons/notification/account_tree.svg';
import SceneEditorIcon from 'src/shared/material_icons/action/view_in_ar.svg';
import SettingsIcon from 'src/shared/material_icons/action/settings.svg';
import styled, { css } from 'styled-components';
import { ProductStatus } from '../../../../../shared/enums';
import { backofficeUrl, T, toLocalID } from '../../../../../shared/helpers';
import { productLogicName } from '../../../../../shared/productNames';
import LogoZakeke from '../../../../../shared/svg/logoZakeke';
import { showErrorNotification, showInfoNotification, showSuccessNotification } from '../../../../widgets/notificationToaster';
import { puSwitchProductSetStatusMutation } from '../../../products/__generated__/puSwitchProductSetStatusMutation.graphql';
import { productStatusMutation } from '../../../products/productStatusMutation';
import { Preview3DStoreContext } from '../../../../widgets/preview3D/store';
import { editProductNameAction, setProductStatusAction, useProductConfigurationChangeStep, useProductConfigurationState } from '../../state';
import { useInlineRename } from './useInlineRename';

type ComposerMode = 'scene' | 'composer' | 'test';

const HeaderContainer = styled.header`
    display: grid;
    height: 52px;
    flex: 0 0 52px;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    gap: 14px;
    padding: 0 18px;
    color: var(--composer-fg, #1e2a38);
    background: linear-gradient(rgb(0 0 0 / 6%), rgb(0 0 0 / 6%)), #fff;
    font-family:
        Inter,
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        system-ui,
        sans-serif;
    z-index: 40;
`;

const HeaderLeft = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
`;

const HeaderRight = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
`;

/* The full Zakeke wordmark, the way the rest of the backoffice signs its pages: cropped to a
   square it showed the Z alone, which reads as an app icon rather than as the product. It is a
   mark, not a way out — the way out is the arrow before it, which asks about unsaved work first.
   24px is the AI Studio header size, the bar this one resembles. */
const Logo = styled.span`
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;

    > svg {
        width: auto;
        height: 24px;
    }
`;

const IdentityDivider = styled.span`
    width: 1px;
    height: 20px;
    flex: 0 0 auto;
    margin: 0 4px;
    background: var(--composer-line, #dbe2e6);
`;

const Identity = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
`;

const ProductName = styled.div`
    max-width: 220px;
    overflow: hidden;
    font-size: 12.5px;
    font-weight: 600;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ProductNameRow = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 2px;
`;

/* Il campo prende il posto del nome con la sua misura: stesso corpo, stesso peso, nessun salto. */
const ProductNameInput = styled.input`
    width: 220px;
    min-width: 0;
    margin-left: -6px;
    padding: 2px 6px;
    color: inherit;
    background: var(--composer-sunken, #f4f7fa);
    border: 1px solid var(--composer-accent, #e8650a);
    border-radius: 5px;
    outline: none;
    font: inherit;
    font-size: 12.5px;
    font-weight: 600;
    line-height: 18px;
`;

const RenameButton = styled(GhostButton).attrs({ $size: 'small', $type: 'icon' })`
    flex: 0 0 auto;
    color: var(--composer-muted, #5c6b7a);

    ${Icon} {
        width: 14px;
        height: 14px;
        fill: currentColor;
    }
`;

// L'USCITA STA A SINISTRA, prima del marchio (QA, 2026-10-12). Qui si lavora in una sezione della
// piattaforma, non in una finestra: chi cerca come tornare guarda dove comincia la riga, ed è anche
// dove la metteva il backoffice di prima. Fa la stessa domanda di prima sulle modifiche non salvate.
const BackButton = styled(GhostButton).attrs({ $size: 'medium', $type: 'icon' })`
    flex: 0 0 auto;

    ${Icon} {
        width: 20px;
        height: 20px;
        fill: currentColor;
    }
`;

const ProductStatusLabel = styled.span<{ $published: boolean }>`
    display: inline-flex;
    height: 24px;
    flex: 0 0 auto;
    align-items: center;
    gap: 6px;
    padding: 0 8px;
    color: var(--composer-muted, #5c6b7a);
    background: var(--composer-sunken, #f4f7fa);
    border-radius: 999px;
    box-shadow: inset 0 0 0 1px var(--composer-line, #dce3eb);
    font-size: 10.5px;

    &::before {
        width: 6px;
        height: 6px;
        background: ${(props) => (props.$published ? '#177c4c' : 'var(--composer-faint, #677078)')};
        border-radius: 50%;
        content: '';
    }
`;

// SETTINGS E IL CAMBIO DI AMBIENTE SONO OUTLINE (utente, 2026-10-15): bordo grigio e fondo bianco, al
// passaggio si accende solo il bordo. Tre riquadri arancioni in fila appesantivano la testata; resta
// Basic soltanto Test mode, che è il passo verso il cliente. Settings è quadrato perché non ha parola,
// e l'icona più grande per pesare quanto una parola.
const SettingsButton = styled(OutlineButton).attrs({ $size: 'medium', $type: 'icon' })`
    flex: 0 0 auto;

    ${Icon},
    > svg {
        width: 20px;
        height: 20px;
        fill: currentColor;
    }
`;

const ModeSwitcher = styled(OutlineButton).attrs({ $size: 'medium' })`
    gap: 8px;
    white-space: nowrap;
    border-radius: 8px;

    ${Icon},
    > svg {
        width: 17px;
        height: 17px;
        fill: currentColor;
    }
`;

const SaveState = styled.span<{ $draft?: boolean; $failed?: boolean }>`
    display: inline-flex;
    height: 24px;
    align-items: center;
    gap: 6px;
    margin: 0 6px 0 2px;
    padding: ${(props) => (props.$draft ? '0 9px' : '0')};
    color: var(--composer-faint, #677078);
    background: ${(props) => (props.$failed ? '#fff0f0' : props.$draft ? 'var(--composer-warn-wash, #fff0df)' : 'transparent')};
    border-radius: 999px;
    font-size: 11.5px;
    white-space: nowrap;

    ${(props) =>
        props.$draft &&
        css`
            color: ${props.$failed ? '#a52424' : 'var(--composer-warn-ink, #874b0b)'};

            &::before {
                width: 6px;
                height: 6px;
                flex: 0 0 6px;
                background: currentColor;
                border-radius: 50%;
                content: '';
            }
        `}
`;

/* Test mode is the shared Basic button at top-bar density: the palette, the hover and the pressed
   wash are the component's own, so it ages with it. */
const HeaderButton = styled(BasicButton).attrs({ $size: 'medium' })<{ $active?: boolean }>`
    white-space: nowrap;

    ${(props) =>
        props.$active &&
        css`
            background-color: ${props.theme.app.component.button.basic.active.backgroundColor};
            color: ${props.theme.app.component.button.basic.active.textColor};
        `}

    /* Stessa misura delle icone del selettore accanto: tre tasti in fila, un'icona sola. */
    > svg {
        width: 17px;
        height: 17px;
        flex: 0 0 17px;
        fill: currentColor;
    }
`;

const Split = styled.div`
    display: flex;
    align-items: stretch;
`;

const SaveButton = styled.button<{ $caret?: boolean }>`
    display: inline-flex;
    height: 32px;
    align-items: center;
    justify-content: center;
    gap: ${(props) => (props.$caret ? '0' : '6px')};
    padding: ${(props) => (props.$caret ? '0 7px' : '0 13px')};
    color: #fff;
    background: var(--composer-accent, #e8650a);
    border: 0;
    border-radius: ${(props) => (props.$caret ? '0 8px 8px 0' : '8px 0 0 8px')};
    box-shadow: ${(props) => (props.$caret ? 'inset 1px 0 0 var(--composer-accent-deep, #c25309)' : 'none')};
    cursor: pointer;
    font: inherit;
    font-size: 12px;
    font-weight: 600;

    &:hover:not(:disabled),
    &:focus-visible {
        background: var(--composer-accent-deep, #c25309);
        outline: none;
    }

    &:disabled {
        cursor: default;
        opacity: 0.55;
    }

    svg {
        width: 12px;
        height: 12px;
        fill: currentColor;
    }
`;

type HeaderToolbarProps = {
    activeMode?: ComposerMode;
    closeOverlay?: () => void;
    onSettings?: () => void;
    onSave?: () => boolean | void | Promise<boolean | void>;
    isSaving?: boolean;
    hasUnsavedChanges?: boolean;
    localDraftState?: 'none' | 'saving' | 'saved' | 'failed';
    /** Il prodotto ha un nome nuovo: chi tiene il «da salvare» lo sappia. */
    onProductRenamed?: () => void;
};

const PRODUCT_NAME_FIELD = 'product-name';

// USCIRE CON MODIFICHE NON SALVATE, dalla Close o cambiando editor: la stessa domanda, con un titolo che
// dice di cosa si parla e un'azione che dice cosa succede. Si resta dalla X, come in tutte le finestre
// della scena e del Composer.
const UnsavedChangesDialog: FC<{ message: string; onClose: () => void; onLeave: () => void }> = ({ message, onClose, onLeave }) => (
    <Dialog
        title={T._('Unsaved changes', 'Admin')}
        size='small'
        closeOnEscape
        onClose={onClose}
        buttons={[{ label: T._('Leave without saving', 'Admin'), destructive: true, onClick: onLeave }]}
    >
        <p>{message}</p>
    </Dialog>
);

const HeaderToolbar: FC<HeaderToolbarProps> = ({
    activeMode = 'composer',
    closeOverlay,
    onSettings,
    onSave,
    isSaving,
    hasUnsavedChanges = false,
    localDraftState = 'none',
    onProductRenamed
}) => {
    const { state, dispatch, unsavedChanges } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();

    // IL NOME DEL PRODOTTO SI CAMBIA DOVE SI LEGGE (QA, 2026-10-12): un prodotto nato da zero salta il
    // passo del wizard che lo rinomina, e qui non c'era modo di dargli un nome suo.
    //
    // E STA IN DUE POSTI: lo stato della configurazione, che salva lo Scene Editor, e il modello 3D, che
    // salvano Product Logic e il VTO. Scritto in uno solo, il prossimo salvataggio dell'altro lo
    // rimetterebbe com'era. Il modello si legge al momento del gesto, dallo store che lo tiene.
    //
    // SOLO PER I PRODOTTI NATI DA ZERO (Elena, 2026-10-12): sono quelli del canale senza store
    // (`NONE`). Un prodotto di uno store prende il nome dallo store, e qui non si tocca.
    const preview3DStore = useContext(Preview3DStoreContext);
    const canRename = state.ecommerceSalesChannel.includes('NONE');
    const rename = useInlineRename();
    const startRename = () => rename.start(PRODUCT_NAME_FIELD, state.product.name);
    const renameProduct = (name: string) => {
        dispatch(editProductNameAction(name));
        preview3DStore?.getState().zkProduct?.set('name', name);
        onProductRenamed?.();
    };
    const [setStatusProduct, isUpdatingStatus] = useMutation<puSwitchProductSetStatusMutation>(productStatusMutation);
    const changeStep = useProductConfigurationChangeStep();
    const navigate = useNavigate();
    const location = useLocation();
    const isDirty = hasUnsavedChanges || unsavedChanges.isDirty;
    const isPublished = state.product.statusID === ProductStatus.Published;
    const isBusy = Boolean(isSaving || isUpdatingStatus);
    const unsavedExitMessage =
        localDraftState === 'failed'
            ? T._('Your changes are not saved, and this browser could not update the local draft.', 'Admin')
            : localDraftState !== 'none'
              ? T._('Your changes are kept as a local draft, but they are not saved to the product yet.', 'Admin')
              : T._('You will lose all unsaved changes.', 'Admin');
    const editorModes = [
        {
            key: 'scene' as const,
            icon: <SceneEditorIcon />,
            name: T._('3D Scene Editor', 'Admin'),
            description: T._('Objects, materials, lights and cameras', 'Admin')
        },
        {
            key: 'composer' as const,
            icon: <ProductLogicIcon />,
            name: productLogicName(),
            description: T._('Groups, attributes, and what an option does', 'Admin')
        }
    ];
    // In test mode neither editor is current: the switcher keeps naming where Test mode came from, and Test mode itself lights up.
    const currentMode = editorModes.find((mode) => mode.key === activeMode) ?? editorModes[1];

    const enterMode = (mode: ComposerMode) => {
        closeOverlay?.();
        if (mode === 'scene') changeStep('3D-product-configurator-scene-editor');
        if (mode === 'composer') changeStep('3D-product-configurator-composer');
        if (mode === 'test') {
            const productID = toLocalID(state.product.id);
            if (productID != null) {
                const params = new URLSearchParams({ returnTo: `${location.pathname}${location.search}` });
                navigate(`${backofficeUrl(`/test-mode/${productID}`)}?${params.toString()}`);
            }
        }
    };

    // CAMBIARE EDITOR È USCIRE DA QUESTO, come premere Close: quel che non è salvato resta indietro
    // uguale. La domanda quindi è la stessa, e si fa prima di andarsene — non dopo.
    const goToMode = (mode: ComposerMode) => {
        if (mode === activeMode) return;
        if (!isDirty) {
            enterMode(mode);
            return;
        }
        showDialog(
            'switch-mode-dialog',
            <UnsavedChangesDialog
                message={unsavedExitMessage}
                onClose={() => closeDialog('switch-mode-dialog')}
                onLeave={() => {
                    closeDialog('switch-mode-dialog');
                    enterMode(mode);
                }}
            />
        );
    };

    // IL PANNELLO SI CHIUDE SOLO USCENDO DAVVERO: chiuso prima della domanda, chi poi resta trovava
    // l'Inspector vuoto.
    const requestClose = () => {
        if (!isDirty) {
            closeOverlay?.();
            navigate(backofficeUrl('/products'));
            return;
        }
        showDialog(
            'close-dialog',
            <UnsavedChangesDialog
                message={unsavedExitMessage}
                onClose={() => closeDialog('close-dialog')}
                onLeave={() => {
                    closeDialog('close-dialog');
                    closeOverlay?.();
                    navigate(backofficeUrl('/products'));
                }}
            />
        );
    };

    const saveChanges = async (notifyWhenClean = true) => {
        if (!isDirty) {
            if (notifyWhenClean) showInfoNotification({ message: T._('Nothing to save — everything is up to date.', 'Admin') });
            return true;
        }
        if (!onSave) {
            showErrorNotification({ message: T._('Saving is not available in this view.', 'Admin') });
            return false;
        }

        try {
            const saved = await onSave();
            return saved !== false;
        } catch {
            return false;
        }
    };

    const changePublicationStatus = async (nextStatus: ProductStatus) => {
        if (!state.product.id || isBusy) return;
        if (isDirty && !(await saveChanges(false))) return;

        const publishing = nextStatus === ProductStatus.Published;
        if (publishing && state.ecommerceSalesChannel[0] === 'NONE') {
            navigate(`${backofficeUrl(`/test-mode/${toLocalID(state.product.id)}`)}?publish=true`);
            return;
        }
        const changed = await new Promise<boolean>((resolve) => {
            setStatusProduct({
                variables: {
                    input: {
                        productID: state.product.id!,
                        status: publishing ? 'PUBLISHED' : 'UN_PUBLISHED'
                    }
                },
                onCompleted: (response, errors) => {
                    const messages = [
                        ...(errors?.map((error) => error.message) ?? []),
                        ...(response.setStatusProduct?.errors?.map((error) => error.message).filter(Boolean) ?? [])
                    ];
                    if (!response.setStatusProduct?.model || messages.length > 0) {
                        showErrorNotification({
                            message:
                                messages.join(' ') ||
                                (publishing ? T._('Failed to publish the product.', 'Admin') : T._('Failed to unpublish the product.', 'Admin'))
                        });
                        resolve(false);
                        return;
                    }

                    dispatch(setProductStatusAction(nextStatus));
                    resolve(true);
                },
                onError: () => {
                    showErrorNotification({
                        message: publishing ? T._('Failed to publish the product.', 'Admin') : T._('Failed to unpublish the product.', 'Admin')
                    });
                    resolve(false);
                }
            });
        });

        if (changed) {
            showSuccessNotification({
                message: publishing ? T._('Product published.', 'Admin') : T._('Product unpublished.', 'Admin')
            });
        }
    };

    return (
        <HeaderContainer>
            <HeaderLeft>
                <ZTooltip title={T._('Back to products', 'Admin')} position='bottom'>
                    <BackButton type='button' aria-label={T._('Back to products', 'Admin')} onClick={requestClose}>
                        <Icon>
                            <ArrowBackIcon />
                        </Icon>
                    </BackButton>
                </ZTooltip>
                <Logo role='img' aria-label='Zakeke'>
                    <LogoZakeke />
                </Logo>
                <IdentityDivider aria-hidden='true' />
                <Identity>
                    {canRename && rename.editingId === PRODUCT_NAME_FIELD ? (
                        <ProductNameInput aria-label={T._('Product name', 'Admin')} {...rename.fieldProps(state.product.name, renameProduct)} />
                    ) : (
                        <ProductNameRow>
                            <ProductName title={state.product.name} onDoubleClick={canRename ? startRename : undefined}>
                                {state.product.name || T._('Product configurator', 'Admin')}
                            </ProductName>
                            {canRename && (
                                <ZTooltip title={T._('Rename product', 'Admin')} position='bottom'>
                                    <RenameButton type='button' aria-label={T._('Rename product', 'Admin')} onClick={startRename}>
                                        <Icon>
                                            <EditIcon />
                                        </Icon>
                                    </RenameButton>
                                </ZTooltip>
                            )}
                        </ProductNameRow>
                    )}
                    <ProductStatusLabel $published={isPublished}>{isPublished ? T._('Published', 'Admin') : T._('Not published', 'Admin')}</ProductStatusLabel>
                </Identity>
            </HeaderLeft>
            <DropdownActions
                header={
                    <ModeSwitcher type='button' aria-label={T._('Configurator mode', 'Admin')}>
                        <Icon>{currentMode.icon}</Icon>
                        {currentMode.name}
                        <ExpandIcon />
                    </ModeSwitcher>
                }
                items={editorModes.map((mode) => (
                    <DropdownActionItem
                        key={mode.key}
                        caption={mode.description}
                        icon={mode.icon}
                        selected={activeMode === mode.key}
                        onClick={() => goToMode(mode.key)}
                    >
                        {mode.name}
                    </DropdownActionItem>
                ))}
                minWidth='280px'
            />
            <HeaderRight>
                <ZTooltip title={T._('Settings', 'Admin')} position='bottom'>
                    <SettingsButton aria-label={T._('Settings', 'Admin')} onClick={onSettings}>
                        <Icon>
                            <SettingsIcon />
                        </Icon>
                    </SettingsButton>
                </ZTooltip>
                {/* TEST MODE È UNO SOLO PER TUTTA LA PIATTAFORMA (utente, 2026-09-22): è la prova del
                    cliente, e da qualunque ambiente si parta è la stessa prova. Per un po' nello Scene
                    Editor questo tasto ha fatto un'altra cosa — accendere il fondale — e due tasti con lo
                    stesso nome che fanno cose diverse sono il modo più rapido per non fidarsi più di
                    nessuno dei due. Il fondale adesso è un interruttore sul palco, dove stanno gli altri
                    modi di guardare. */}
                <HeaderButton $active={activeMode === 'test'} onClick={() => goToMode('test')}>
                    <PlayIcon />
                    {T._('Test mode', 'Admin')}
                </HeaderButton>
                <SaveState
                    $draft={isDirty && localDraftState !== 'none'}
                    $failed={localDraftState === 'failed'}
                    aria-live='polite'
                    title={
                        isDirty && localDraftState !== 'none'
                            ? T._('This browser has a temporary copy. Use Save to persist it to the product history.', 'Admin')
                            : undefined
                    }
                >
                    {isSaving
                        ? T._('Saving…', 'Admin')
                        : isUpdatingStatus
                          ? T._('Updating publication…', 'Admin')
                          : isDirty && localDraftState === 'saving'
                            ? T._('Saving local draft…', 'Admin')
                            : isDirty && localDraftState === 'saved'
                              ? T._('Local draft · Not saved', 'Admin')
                              : isDirty && localDraftState === 'failed'
                                ? T._('Draft · Local save failed', 'Admin')
                                : isDirty
                                  ? T._('Unsaved changes', 'Admin')
                                  : T._('Saved', 'Admin')}
                </SaveState>
                <Split>
                    <SaveButton disabled={isBusy} onClick={() => void saveChanges()}>
                        <SaveIcon />
                        {isSaving ? T._('Saving…', 'Admin') : T._('Save', 'Admin')}
                    </SaveButton>
                    <DropdownActions
                        header={({ isOpen }) => (
                            <SaveButton $caret disabled={isBusy} aria-expanded={isOpen} aria-label={T._('More save options', 'Admin')}>
                                {isOpen ? <CollapseIcon /> : <ExpandIcon />}
                            </SaveButton>
                        )}
                        items={[
                            <DropdownActionItem key='save' caption={T._('Keep your changes', 'Admin')} disabled={isBusy} onClick={() => void saveChanges()}>
                                {T._('Save', 'Admin')}
                            </DropdownActionItem>,
                            <DropdownActionItem
                                key='publish'
                                caption={T._('Put this product in the storefront', 'Admin')}
                                disabled={isBusy || isPublished || !state.product.id}
                                onClick={() => void changePublicationStatus(ProductStatus.Published)}
                            >
                                {T._('Publish', 'Admin')}
                            </DropdownActionItem>,
                            <DropdownActionItem
                                key='unpublish'
                                caption={T._('Take it out of the storefront', 'Admin')}
                                disabled={isBusy || !isPublished || !state.product.id}
                                onClick={() => void changePublicationStatus(ProductStatus.UnPublished)}
                            >
                                {T._('Unpublish', 'Admin')}
                            </DropdownActionItem>
                        ]}
                        minWidth='220px'
                        orientation='RIGHT'
                    />
                </Split>
            </HeaderRight>
        </HeaderContainer>
    );
};

export default HeaderToolbar;
